import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { AccessibilityDTO } from '../../dto/accessibility-dto.model';
// API endpoint for accessibility configuration
const API_ACCESSIBILITY_ENDPOINT = 'api/configuration/accessibility';

export interface AccessibilityContent {
  content: string;
  isAccessibilityEnabled: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AccessibilityService {
  private readonly _http = inject(HttpClient);
  private readonly _accessibilityContent = new BehaviorSubject<AccessibilityContent | null>(null);

  /**
   * Observable stream of accessibility content
   */
  public readonly accessibilityContent$ = this._accessibilityContent.asObservable();

  /**
   * Get current accessibility content from the server
   */
  getAccessibilityContent(): Observable<AccessibilityContent> {
    return this._http.get<AccessibilityDTO & { isAccessibilityEnabled: boolean }>(API_ACCESSIBILITY_ENDPOINT)
      .pipe(
        map(response => ({
          content: response.content || '',
          isAccessibilityEnabled: response.isAccessibilityEnabled ?? false
        })),
        tap(content => this._accessibilityContent.next(content))
      );
  }

  /**
   * Get accessibility content for admin (includes content even when disabled)
   */
  getAccessibilityContentForAdmin(): Observable<AccessibilityContent> {
    return this._http.get<AccessibilityDTO & { isAccessibilityEnabled: boolean }>(`${API_ACCESSIBILITY_ENDPOINT}/admin`)
      .pipe(
        map(response => ({
          content: response.content || '',
          isAccessibilityEnabled: response.isAccessibilityEnabled ?? false
        })),
        tap(content => this._accessibilityContent.next(content))
      );
  }

  /**
   * Save accessibility content (admin function)
   */
  saveAccessibilityContent(content: string): Observable<AccessibilityContent> {
    // Encode content to Base64 before sending to server (like terms system)
    const encodedContent = this.encodeToBase64(content);
    const payload = { content: encodedContent };
    return this._http.put<AccessibilityDTO & { isAccessibilityEnabled: boolean }>(API_ACCESSIBILITY_ENDPOINT, payload)
      .pipe(
        map(response => ({
          content: response.content || '',
          isAccessibilityEnabled: response.isAccessibilityEnabled ?? false
        })),
        tap(content => this._accessibilityContent.next(content))
      );
  }

  /**
   * Toggle accessibility feature on/off (admin function)
   */
  toggleAccessibilityStatus(enabled: boolean): Observable<AccessibilityContent> {
    // Get current content to preserve it when toggling
    const currentContent = this._accessibilityContent.value?.content || '';
    // Encode content to Base64 before sending (like saveAccessibilityContent does)
    const encodedContent = currentContent ? this.encodeToBase64(currentContent) : '';
    const payload = { 
      content: encodedContent,
      isAccessibilityEnabled: enabled 
    };
    return this._http.put<AccessibilityDTO & { isAccessibilityEnabled: boolean }>(API_ACCESSIBILITY_ENDPOINT, payload)
      .pipe(
        map(response => ({
          content: response.content || '',
          isAccessibilityEnabled: response.isAccessibilityEnabled ?? false
        })),
        tap(content => this._accessibilityContent.next(content))
      );
  }

  /**
   * Get current cached accessibility content
   */
  getCurrentContent(): AccessibilityContent | null {
    return this._accessibilityContent.value;
  }

  /**
   * Clear cached accessibility content
   */
  clearCache(): void {
    this._accessibilityContent.next(null);
  }

  /**
   * Encode content to Base64 for secure transmission
   */
  private encodeToBase64(content: string): string {
    try {
      // Use btoa for Base64 encoding, handle Unicode characters properly
      return btoa(
        encodeURIComponent(content).replaceAll(
          /%([0-9A-Fa-f]{2})/g,
          (_, p1) => String.fromCodePoint(Number.parseInt(p1, 16))
        )
      );
    } catch (error) {
      console.error('Failed to encode content to Base64:', error);     
       throw new Error('Failed to encode content for transmission');
    }
  }
}
